// src/pages/components/CommunityChatTab.jsx

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

/* =========================================================
   CONSTANTS
========================================================= */

const API_BASE =
    "https://oppiesfunc.azurewebsites.net/api";

const MAX_POST_LENGTH = 500;

const MAX_IMAGE_SIZE =
    10 * 1024 * 1024;

const MAX_IMAGES = 4;

/* =========================================================
   LIVE AZURE FUNCTION KEYS
========================================================= */

const KEYS = {
    listFeedPosts:
        "09_jAwtFqWaNVfzcDAj-yOA9T1ztNZ8A9rJ9rvjUN1dTAzFuSiqahw==",

    listComments:
        "MKnIdHef4Wy2Y6swVfvxaKah2IL1EWx9_esvNfiDPqzUAzFubOe0ww==",

    createFeedPost:
        "OK5em83a83JzJzsiqLT0wXPizwljwYPrilonaig9p0tiAzFuinb6Ng==",

    likeFeedPost:
        "FgMaAAj93bfm8kte7L9UnNHXhyjKA30_70gFYhPLSk_VAzFuT0N0yg==",

    commentOnPost:
        "DsUQsre4fnByuY-wzX8AbcpW2FZUtAsfSganon9-HTu8AzFuu4dJZw==",

    manageFeedPost:
        "HL83ygo_-UBncXE03GNA4wrhsMWc6PZIvGns6XkdraL0AzFuDLmidw==",

    manageComment:
        "x74z5GZYiLPzqOZxYkBrB0p3xhECmTtazfOBIinBWstMAzFuxgJqtw==",

    getFeedImageUploadUrl:
        "K8CuIU_Yd9mcyURHZO5XqIJeRKNt7W0IDbzL9WK_Z-QRAzFu0BFnDw==",
};

/* =========================================================
   UTILITIES
========================================================= */

const relativeTime = (value) => {
    if (!value) {
        return "";
    }

    const time =
        new Date(value).getTime();

    if (!Number.isFinite(time)) {
        return "";
    }

    const difference =
        Math.max(
            0,
            Math.floor(
                (Date.now() - time) /
                1000
            )
        );

    const result = (
        number,
        unit
    ) =>
        `${number} ${unit}${number !== 1 ? "s" : ""} ago`;

    if (difference < 10) {
        return "Just now";
    }

    if (difference < 60) {
        return result(
            difference,
            "second"
        );
    }

    const minutes =
        Math.floor(
            difference / 60
        );

    if (minutes < 60) {
        return result(
            minutes,
            "minute"
        );
    }

    const hours =
        Math.floor(
            minutes / 60
        );

    if (hours < 24) {
        return result(
            hours,
            "hour"
        );
    }

    const days =
        Math.floor(
            hours / 24
        );

    if (days < 30) {
        return result(
            days,
            "day"
        );
    }

    const months =
        Math.floor(
            days / 30
        );

    if (months < 12) {
        return result(
            months,
            "month"
        );
    }

    return result(
        Math.floor(
            months / 12
        ),
        "year"
    );
};

/* =========================================================
   NORMALISE POST
========================================================= */

const normalisePost = (
    post
) => {
    const rawMedia =
        Array.isArray(
            post?.media
        )
            ? post.media
            : [];

    const media =
        rawMedia
            .map(
                (
                    item,
                    index
                ) => {
                    if (
                        typeof item ===
                        "string"
                    ) {
                        const url =
                            item.trim();

                        if (!url) {
                            return null;
                        }

                        return {
                            type:
                                "image",
                            url,
                            order:
                                index,
                        };
                    }

                    if (
                        item &&
                        typeof item ===
                        "object"
                    ) {
                        const url =
                            String(
                                item.url ||
                                item.imageUrl ||
                                item.imageURL ||
                                ""
                            ).trim();

                        if (!url) {
                            return null;
                        }

                        return {
                            ...item,

                            type:
                                "image",

                            url,

                            order:
                                Number.isFinite(
                                    Number(
                                        item.order
                                    )
                                )
                                    ? Number(
                                        item.order
                                    )
                                    : index,
                        };
                    }

                    return null;
                }
            )
            .filter(Boolean)
            .sort(
                (a, b) =>
                    Number(
                        a.order || 0
                    ) -
                    Number(
                        b.order || 0
                    )
            );

    const fallbackImage =
        post?.imageUrl ||
        post?.imageURL ||
        null;

    /*
     * Backwards compatibility:
     *
     * Older posts only have imageUrl.
     */

    if (
        media.length === 0 &&
        fallbackImage
    ) {
        media.push({
            type:
                "image",

            url:
                fallbackImage,

            order:
                0,
        });
    }

    return {
        ...post,

        authorName:
            post?.authorName ||
            post?.authorDisplayName ||
            post?.userName ||
            post?.actorName ||
            "OPPIES user",

        imageUrl:
            fallbackImage ||
            media?.[0]?.url ||
            null,

        media,

        likeCount:
            Number(
                post?.likeCount ||
                0
            ),

        commentCount:
            Number(
                post?.commentCount ||
                0
            ),
    };
};

/* =========================================================
   NORMALISE COMMENT
========================================================= */

const normaliseComment = (
    comment
) => ({
    ...comment,

    userName:
        comment?.userName ||
        comment?.authorName ||
        "OPPIES user",

    likeCount:
        Number(
            comment?.likeCount ||
            0
        ),
});

/* =========================================================
   API HELPERS
========================================================= */

const apiGet =
    async (path) => {
        const url =
            `${API_BASE}${path}`;

        const response =
            await fetch(
                url,
                {
                    method:
                        "GET",
                }
            );

        if (!response.ok) {
            const text =
                await response
                    .text()
                    .catch(
                        () => ""
                    );

            throw new Error(
                `${response.status} ${response.statusText} — ${text || url}`
            );
        }

        return response.json();
    };

const apiPost =
    async (
        path,
        body
    ) => {
        const url =
            `${API_BASE}${path}`;

        const response =
            await fetch(
                url,
                {
                    method:
                        "POST",

                    headers: {
                        "Content-Type":
                            "application/json",
                    },

                    body:
                        JSON.stringify(
                            body ||
                            {}
                        ),
                }
            );

        if (!response.ok) {
            const text =
                await response
                    .text()
                    .catch(
                        () => ""
                    );

            throw new Error(
                `${response.status} ${response.statusText} — ${text || url}`
            );
        }

        return response.json();
    };

/* =========================================================
   FEED API
========================================================= */

const FeedAPI = {
    listFeedPosts:
        async ({
            limit = 25,
            cursor = "",
            userId = "",
        } = {}) =>
            apiGet(
                `/listFeedPosts?code=${KEYS.listFeedPosts}` +
                `&limit=${limit}` +
                (
                    cursor
                        ? `&cursor=${encodeURIComponent(cursor)}`
                        : ""
                ) +
                (
                    userId
                        ? `&userId=${encodeURIComponent(userId)}`
                        : ""
                ) +
                `&_t=${Date.now()}`
            ),

    listComments:
        async ({
            postId,
            limit = 30,
            cursor = "",
            userId = "",
        } = {}) =>
            apiGet(
                `/listComments?code=${KEYS.listComments}` +
                `&postId=${encodeURIComponent(postId)}` +
                `&limit=${limit}` +
                (
                    cursor
                        ? `&cursor=${encodeURIComponent(cursor)}`
                        : ""
                ) +
                (
                    userId
                        ? `&userId=${encodeURIComponent(userId)}`
                        : ""
                ) +
                `&_t=${Date.now()}`
            ),

    createFeedPost:
        async (body) =>
            apiPost(
                `/createFeedPost?code=${KEYS.createFeedPost}`,
                body
            ),

    likeFeedPost:
        async ({
            postId,
            userId,
            userName,
            userEmail,
            shopId,
            commentId,
            target,
        }) =>
            apiPost(
                `/likeFeedPost?code=${KEYS.likeFeedPost}`,
                {
                    postId,
                    userId,
                    userName,
                    userEmail,
                    shopId,
                    commentId,
                    target,
                }
            ),

    commentOnPost:
        async (body) =>
            apiPost(
                `/commentOnPost?code=${KEYS.commentOnPost}`,
                body
            ),

    manageFeedPost:
        async (body) =>
            apiPost(
                `/manageFeedPost?code=${KEYS.manageFeedPost}`,
                body
            ),

    manageComment:
        async (body) =>
            apiPost(
                `/manageComment?code=${KEYS.manageComment}`,
                body
            ),

    getFeedImageUploadUrl:
        async ({
            userId,
            userEmail,
            shopId,
            ext = "jpg",
            contentType,
        }) =>
            apiGet(
                `/getFeedImageUploadUrl?code=${KEYS.getFeedImageUploadUrl}` +
                `&userId=${encodeURIComponent(userId || "")}` +
                `&userEmail=${encodeURIComponent(userEmail || "")}` +
                `&shopId=${encodeURIComponent(shopId || "")}` +
                `&ext=${encodeURIComponent(ext)}` +
                `&contentType=${encodeURIComponent(contentType)}` +
                `&_t=${Date.now()}`
            ),
};

/* =========================================================
   SAS UPLOAD
========================================================= */

async function uploadToSAS(
    uploadUrl,
    headers,
    file
) {
    const uploadHeaders =
        new Headers(
            headers ||
            {}
        );

    uploadHeaders.set(
        "x-ms-blob-type",
        "BlockBlob"
    );

    if (
        file?.type &&
        !uploadHeaders.has(
            "Content-Type"
        )
    ) {
        uploadHeaders.set(
            "Content-Type",
            file.type
        );
    }

    const response =
        await fetch(
            uploadUrl,
            {
                method:
                    "PUT",

                headers:
                    uploadHeaders,

                body:
                    file,
            }
        );

    if (!response.ok) {
        throw new Error(
            `Blob upload failed: ${response.status} ${response.statusText}`
        );
    }
}

/* =========================================================
   POST MEDIA CAROUSEL
========================================================= */

function PostMedia({
    post,
}) {
    const media =
        Array.isArray(
            post?.media
        )
            ? post.media.filter(
                (item) =>
                    item?.url
            )
            : [];

    const [
        activeIndex,
        setActiveIndex,
    ] = useState(0);

    useEffect(() => {
        if (
            activeIndex >
            media.length - 1
        ) {
            setActiveIndex(
                0
            );
        }
    }, [
        activeIndex,
        media.length,
    ]);

    if (
        media.length ===
        0
    ) {
        return null;
    }

    const goPrevious =
        () => {
            setActiveIndex(
                (current) =>
                    current <= 0
                        ? media.length - 1
                        : current - 1
            );
        };

    const goNext =
        () => {
            setActiveIndex(
                (current) =>
                    current >=
                        media.length - 1
                        ? 0
                        : current + 1
            );
        };

    return (
        <div className="feed-post-media">

            <div className="feed-media-stage">

                <img
                    src={
                        media[
                            activeIndex
                        ]?.url
                    }
                    alt={`Community post ${activeIndex + 1} of ${media.length}`}
                    loading="lazy"
                />

                {media.length >
                    1 && (
                        <>
                            <button
                                type="button"
                                className="feed-media-arrow feed-media-arrow-left"
                                onClick={
                                    goPrevious
                                }
                                aria-label="Previous photo"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    width="18"
                                    height="18"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m15 18-6-6 6-6" />
                                </svg>
                            </button>

                            <button
                                type="button"
                                className="feed-media-arrow feed-media-arrow-right"
                                onClick={
                                    goNext
                                }
                                aria-label="Next photo"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    width="18"
                                    height="18"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m9 18 6-6-6-6" />
                                </svg>
                            </button>

                            <div className="feed-media-counter">
                                {activeIndex + 1}
                                {" / "}
                                {media.length}
                            </div>
                        </>
                    )}

            </div>

            {media.length >
                1 && (
                    <div className="feed-media-dots">

                        {media.map(
                            (
                                item,
                                index
                            ) => (
                                <button
                                    key={`${item.url}-${index}`}
                                    type="button"
                                    className={
                                        index ===
                                            activeIndex
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setActiveIndex(
                                            index
                                        )
                                    }
                                    aria-label={`Show photo ${index + 1}`}
                                />
                            )
                        )}

                    </div>
                )}

        </div>
    );
}

/* =========================================================
   COMPONENT
========================================================= */

export default function CommunityChatTab({
    shopId = "",
    shopEmail = "",
    shopName = "",
    userEmail = "",
    targetPostId = null,
    targetCommentId = null,
}) {
    /* =======================================================
       IDENTITY

       authenticatedEmail = logged-in Portal account
       actorShopId        = selected Dashboard branch
       actorEmail         = selected branch email
       actorName          = selected branch name

       Selected branch remains the PUBLIC feed identity.

       Manager / authenticated account is only used for
       backend authorisation and audit.
    ======================================================= */

    const authenticatedEmail =
        useMemo(
            () =>
                (
                    userEmail ||
                    localStorage.getItem(
                        "shopEmail"
                    ) ||
                    ""
                )
                    .trim()
                    .toLowerCase(),
            [
                userEmail,
            ]
        );

    const actorShopId =
        useMemo(
            () =>
                (
                    shopId ||
                    localStorage.getItem(
                        "shopId"
                    ) ||
                    ""
                ).trim(),
            [
                shopId,
            ]
        );

    const actorEmail =
        useMemo(
            () =>
                (
                    shopEmail ||
                    (
                        actorShopId ===
                            (
                                localStorage.getItem(
                                    "shopId"
                                ) ||
                                ""
                            ).trim()
                            ? localStorage.getItem(
                                "shopEmail"
                            ) ||
                            ""
                            : ""
                    )
                )
                    .trim()
                    .toLowerCase(),
            [
                shopEmail,
                actorShopId,
            ]
        );

    const actorName =
        useMemo(
            () =>
                (
                    shopName ||
                    (
                        actorShopId ===
                            (
                                localStorage.getItem(
                                    "shopId"
                                ) ||
                                ""
                            ).trim()
                            ? localStorage.getItem(
                                "shopName"
                            ) ||
                            ""
                            : ""
                    ) ||
                    "Op Shop"
                ).trim(),
            [
                shopName,
                actorShopId,
            ]
        );

    const currentName =
        actorName;

    /* =======================================================
       FEED STATE
    ======================================================= */

    const [
        posts,
        setPosts,
    ] = useState([]);

    const [
        cursor,
        setCursor,
    ] = useState("");

    const [
        initialLoading,
        setInitialLoading,
    ] = useState(true);

    const [
        loadingMore,
        setLoadingMore,
    ] = useState(false);

    const [
        refreshing,
        setRefreshing,
    ] = useState(false);

    const [
        posting,
        setPosting,
    ] = useState(false);

    const [
        error,
        setError,
    ] = useState("");

    /* =======================================================
       COMPOSER
    ======================================================= */

    const [
        text,
        setText,
    ] = useState("");

    /*
     * Each item:
     *
     * {
     *   id,
     *   file,
     *   preview
     * }
     */

    const [
        files,
        setFiles,
    ] = useState([]);

    const fileRef =
        useRef(null);

    const filesRef =
        useRef([]);

    /* =======================================================
       EDIT POST
    ======================================================= */

    const [
        editingPostId,
        setEditingPostId,
    ] = useState(null);

    const [
        editingText,
        setEditingText,
    ] = useState("");

    /* =======================================================
       DEEP LINK
    ======================================================= */

    const [
        highlightPostId,
        setHighlightPostId,
    ] = useState(null);

    const [
        highlightCommentId,
        setHighlightCommentId,
    ] = useState(null);

    /* =======================================================
       COMPUTED
    ======================================================= */

    const trimmedText =
        text.trim();

    const canPost =
        !posting &&
        (
            trimmedText.length >
            0 ||
            files.length >
            0
        ) &&
        trimmedText.length <=
        MAX_POST_LENGTH;

    const charactersLeft =
        MAX_POST_LENGTH -
        text.length;

    /* =======================================================
       PREVIEW CLEANUP
    ======================================================= */

    const revokePreviews =
        useCallback(
            (
                items = []
            ) => {
                items.forEach(
                    (item) => {
                        if (
                            item?.preview &&
                            item.preview.startsWith(
                                "blob:"
                            )
                        ) {
                            URL.revokeObjectURL(
                                item.preview
                            );
                        }
                    }
                );
            },
            []
        );

    useEffect(() => {
        filesRef.current =
            files;
    }, [
        files,
    ]);

    useEffect(() => {
        return () => {
            revokePreviews(
                filesRef.current
            );
        };
    }, [
        revokePreviews,
    ]);

    /* =======================================================
       FETCH FEED
    ======================================================= */

    const requestFeed =
        useCallback(
            async ({
                cursorValue = "",
            } = {}) => {
                const response =
                    await FeedAPI
                        .listFeedPosts({
                            limit:
                                25,

                            cursor:
                                cursorValue,

                            userId:
                                actorShopId,
                        });

                const raw =
                    Array.isArray(
                        response?.items
                    )
                        ? response.items
                        : [];

                const items =
                    raw.map(
                        normalisePost
                    );

                return {
                    items,

                    nextCursor:
                        response
                            ?.nextCursor ||
                        "",
                };
            },
            [
                actorShopId,
            ]
        );

    /* =======================================================
       SELECTED SHOP CHANGE
    ======================================================= */

    useEffect(() => {
        setText("");

        setFiles(
            (current) => {
                revokePreviews(
                    current
                );

                return [];
            }
        );

        if (
            fileRef.current
        ) {
            fileRef.current.value =
                "";
        }

        setEditingPostId(
            null
        );

        setEditingText("");

        setError("");

        setPosts(
            (previous) =>
                previous.map(
                    (post) => ({
                        ...post,

                        _showComments:
                            false,

                        _comments:
                            undefined,
                    })
                )
        );
    }, [
        actorShopId,
        actorEmail,
        revokePreviews,
    ]);

    /* =======================================================
       INITIAL LOAD
    ======================================================= */

    useEffect(() => {
        let cancelled =
            false;

        const run =
            async () => {
                setError("");

                try {
                    const result =
                        await requestFeed();

                    if (
                        cancelled
                    ) {
                        return;
                    }

                    setPosts(
                        result.items
                    );

                    setCursor(
                        result.nextCursor
                    );
                } catch (err) {
                    if (
                        !cancelled
                    ) {
                        console.error(
                            "Feed load failed:",
                            err
                        );

                        setError(
                            "We couldn't load the Community Feed."
                        );
                    }
                } finally {
                    if (
                        !cancelled
                    ) {
                        setInitialLoading(
                            false
                        );
                    }
                }
            };

        run();

        return () => {
            cancelled =
                true;
        };
    }, [
        requestFeed,
    ]);

    /* =======================================================
       REFRESH FEED
    ======================================================= */

    const refreshFeed =
        useCallback(
            async ({
                showLoader = false,
            } = {}) => {
                try {
                    setError("");

                    if (
                        showLoader
                    ) {
                        setRefreshing(
                            true
                        );
                    }

                    const result =
                        await requestFeed();

                    setPosts(
                        result.items
                    );

                    setCursor(
                        result.nextCursor
                    );

                    return result.items;
                } catch (err) {
                    console.error(
                        "Feed refresh failed:",
                        err
                    );

                    setError(
                        "We couldn't refresh the Community Feed."
                    );

                    return [];
                } finally {
                    if (
                        showLoader
                    ) {
                        setRefreshing(
                            false
                        );
                    }
                }
            },
            [
                requestFeed,
            ]
        );

    /* =======================================================
       LOAD MORE
    ======================================================= */

    const loadMore =
        async () => {
            if (
                !cursor ||
                loadingMore
            ) {
                return;
            }

            try {
                setError("");

                setLoadingMore(
                    true
                );

                const result =
                    await requestFeed({
                        cursorValue:
                            cursor,
                    });

                setPosts(
                    (previous) => {
                        const map =
                            new Map();

                        [
                            ...previous,
                            ...result.items,
                        ].forEach(
                            (post) => {
                                map.set(
                                    post.id,
                                    post
                                );
                            }
                        );

                        return Array.from(
                            map.values()
                        );
                    }
                );

                setCursor(
                    result.nextCursor
                );
            } catch (err) {
                console.error(
                    "Load more failed:",
                    err
                );

                setError(
                    "We couldn't load more posts."
                );
            } finally {
                setLoadingMore(
                    false
                );
            }
        };

    /* =======================================================
       MULTI PHOTO SELECTION
    ======================================================= */

    const clearFiles =
        () => {
            setFiles(
                (current) => {
                    revokePreviews(
                        current
                    );

                    return [];
                }
            );

            if (
                fileRef.current
            ) {
                fileRef.current.value =
                    "";
            }
        };

    const removeFile =
        (fileId) => {
            setFiles(
                (current) => {
                    const target =
                        current.find(
                            (item) =>
                                item.id ===
                                fileId
                        );

                    if (
                        target?.preview &&
                        target.preview.startsWith(
                            "blob:"
                        )
                    ) {
                        URL.revokeObjectURL(
                            target.preview
                        );
                    }

                    return current.filter(
                        (item) =>
                            item.id !==
                            fileId
                    );
                }
            );

            /*
             * Reset input so same image can
             * be reselected if wanted.
             */

            if (
                fileRef.current
            ) {
                fileRef.current.value =
                    "";
            }
        };

    const handleFiles =
        (selectedFiles) => {
            const incoming =
                Array.from(
                    selectedFiles ||
                    []
                );

            if (
                incoming.length ===
                0
            ) {
                return;
            }

            const valid =
                [];

            const problems =
                [];

            incoming.forEach(
                (
                    selectedFile
                ) => {
                    if (
                        !selectedFile.type
                            ?.startsWith(
                                "image/"
                            )
                    ) {
                        problems.push(
                            `${selectedFile.name || "A file"} is not a valid image.`
                        );

                        return;
                    }

                    if (
                        selectedFile.size >
                        MAX_IMAGE_SIZE
                    ) {
                        problems.push(
                            `${selectedFile.name || "An image"} is larger than 10 MB.`
                        );

                        return;
                    }

                    valid.push(
                        selectedFile
                    );
                }
            );

            let limitReached =
                false;

            setFiles(
                (current) => {
                    const available =
                        Math.max(
                            0,
                            MAX_IMAGES -
                            current.length
                        );

                    if (
                        available ===
                        0
                    ) {
                        limitReached =
                            true;

                        return current;
                    }

                    const existingKeys =
                        new Set(
                            current.map(
                                (item) =>
                                    `${item.file.name}:${item.file.size}:${item.file.lastModified}`
                            )
                        );

                    const unique =
                        valid.filter(
                            (selectedFile) =>
                                !existingKeys.has(
                                    `${selectedFile.name}:${selectedFile.size}:${selectedFile.lastModified}`
                                )
                        );

                    if (
                        unique.length >
                        available
                    ) {
                        limitReached =
                            true;
                    }

                    const additions =
                        unique
                            .slice(
                                0,
                                available
                            )
                            .map(
                                (
                                    selectedFile
                                ) => ({
                                    id:
                                        `${selectedFile.name}-${selectedFile.size}-${selectedFile.lastModified}-${Math.random()
                                            .toString(36)
                                            .slice(2, 8)}`,

                                    file:
                                        selectedFile,

                                    preview:
                                        URL.createObjectURL(
                                            selectedFile
                                        ),
                                })
                            );

                    return [
                        ...current,
                        ...additions,
                    ];
                }
            );

            if (
                fileRef.current
            ) {
                fileRef.current.value =
                    "";
            }

            if (
                problems.length >
                0
            ) {
                setError(
                    problems[0]
                );
            } else if (
                limitReached ||
                incoming.length >
                MAX_IMAGES
            ) {
                setError(
                    `You can add up to ${MAX_IMAGES} photos to one post.`
                );
            } else {
                setError("");
            }
        };

    /* =======================================================
       CREATE POST
    ======================================================= */

    const handleSubmit =
        async () => {
            if (!canPost) {
                return;
            }

            if (
                !authenticatedEmail ||
                !actorShopId
            ) {
                setError(
                    "Your shop session could not be found. Please sign in again."
                );

                return;
            }

            try {
                setPosting(
                    true
                );

                setError("");

                const uploadedMedia =
                    [];

                /*
                 * Upload photos one at a time.
                 *
                 * Each gets its own SAS URL.
                 */

                for (
                    const selected
                    of files
                ) {
                    const file =
                        selected.file;

                    const contentType =
                        file.type ||
                        "image/jpeg";

                    const extension =
                        (
                            file.name ||
                            "photo.jpg"
                        )
                            .split(".")
                            .pop()
                            ?.toLowerCase() ||
                        "jpg";

                    const sas =
                        await FeedAPI
                            .getFeedImageUploadUrl({
                                userId:
                                    actorShopId,

                                userEmail:
                                    authenticatedEmail,

                                shopId:
                                    actorShopId,

                                ext:
                                    extension,

                                contentType,
                            });

                    if (
                        !sas?.uploadUrl
                    ) {
                        throw new Error(
                            `Image upload URL was not returned for ${file.name || "one of the selected photos"}.`
                        );
                    }

                    await uploadToSAS(
                        sas.uploadUrl,
                        sas.headers,
                        file
                    );

                    const blobUrl =
                        sas.blobUrl ||
                        sas.uploadUrl
                            .split(
                                "?"
                            )[0];

                    uploadedMedia.push({
                        type:
                            "image",

                        url:
                            blobUrl,

                        order:
                            uploadedMedia.length,
                    });
                }

                const firstImageUrl =
                    uploadedMedia
                        ?.[0]
                        ?.url ||
                    null;

                await FeedAPI
                    .createFeedPost({
                        /*
                         * PUBLIC selected shop
                         */

                        authorId:
                            actorShopId,

                        authorEmail:
                            actorEmail ||
                            null,

                        authorType:
                            "opshop",

                        authorName:
                            actorName,

                        shopId:
                            actorShopId,

                        /*
                         * Authenticated account
                         * for manager security.
                         */

                        userEmail:
                            authenticatedEmail,

                        text:
                            trimmedText,

                        /*
                         * Backwards compatibility.
                         */

                        imageUrl:
                            firstImageUrl,

                        /*
                         * New carousel.
                         */

                        media:
                            uploadedMedia,
                    });

                setText("");

                clearFiles();

                await refreshFeed();

            } catch (err) {
                console.error(
                    "Create post failed:",
                    err
                );

                setError(
                    err?.message ||
                    "We couldn't publish your post."
                );
            } finally {
                setPosting(
                    false
                );
            }
        };

    /* =======================================================
       EDIT POST
    ======================================================= */

    const startEditPost =
        (post) => {
            setEditingPostId(
                post.id
            );

            setEditingText(
                post.text ||
                ""
            );
        };

    const cancelEdit =
        () => {
            setEditingPostId(
                null
            );

            setEditingText("");
        };

    const saveEdit =
        async () => {
            if (
                !editingPostId
            ) {
                return;
            }

            const cleanText =
                editingText.trim();

            if (
                cleanText.length >
                MAX_POST_LENGTH
            ) {
                setError(
                    `Posts can be a maximum of ${MAX_POST_LENGTH} characters.`
                );

                return;
            }

            try {
                setError("");

                await FeedAPI
                    .manageFeedPost({
                        action:
                            "edit",

                        postId:
                            editingPostId,

                        userId:
                            actorShopId,

                        userEmail:
                            authenticatedEmail,

                        shopId:
                            actorShopId,

                        text:
                            cleanText,
                    });

                setEditingPostId(
                    null
                );

                setEditingText("");

                await refreshFeed();

            } catch (err) {
                console.error(
                    "Edit post failed:",
                    err
                );

                setError(
                    err?.message ||
                    "We couldn't update your post."
                );
            }
        };

    /* =======================================================
       DELETE POST
    ======================================================= */

    const deletePost =
        async (
            postId
        ) => {
            const confirmed =
                window.confirm(
                    "Delete this post?"
                );

            if (!confirmed) {
                return;
            }

            try {
                setError("");

                await FeedAPI
                    .manageFeedPost({
                        action:
                            "delete",

                        postId,

                        userId:
                            actorShopId,

                        userEmail:
                            authenticatedEmail,

                        shopId:
                            actorShopId,
                    });

                setPosts(
                    (previous) =>
                        previous.filter(
                            (post) =>
                                post.id !==
                                postId
                        )
                );

            } catch (err) {
                console.error(
                    "Delete post failed:",
                    err
                );

                setError(
                    err?.message ||
                    "We couldn't delete your post."
                );
            }
        };

    /* =======================================================
       LIKE POST
    ======================================================= */

    const toggleLikePost =
        async (
            post
        ) => {
            const originalLiked =
                !!post.likedByMe;

            const originalCount =
                Number(
                    post.likeCount ||
                    0
                );

            const nextLiked =
                !originalLiked;

            const nextCount =
                Math.max(
                    0,
                    originalCount +
                    (
                        nextLiked
                            ? 1
                            : -1
                    )
                );

            setPosts(
                (previous) =>
                    previous.map(
                        (item) =>
                            item.id ===
                                post.id
                                ? {
                                    ...item,

                                    likedByMe:
                                        nextLiked,

                                    likeCount:
                                        nextCount,
                                }
                                : item
                    )
            );

            try {
                await FeedAPI
                    .likeFeedPost({
                        postId:
                            post.id,

                        userId:
                            actorShopId,

                        userName:
                            actorName,

                        userEmail:
                            authenticatedEmail,

                        shopId:
                            actorShopId,
                    });

            } catch (err) {
                setPosts(
                    (previous) =>
                        previous.map(
                            (item) =>
                                item.id ===
                                    post.id
                                    ? {
                                        ...item,

                                        likedByMe:
                                            originalLiked,

                                        likeCount:
                                            originalCount,
                                    }
                                    : item
                        )
                );

                setError(
                    err?.message ||
                    "We couldn't update that like."
                );
            }
        };

    /* =======================================================
       LOAD COMMENTS
    ======================================================= */

    const loadComments =
        async (
            postId,
            limit = 30
        ) => {
            const response =
                await FeedAPI
                    .listComments({
                        postId,

                        limit,

                        userId:
                            actorShopId,
                    });

            return Array.isArray(
                response?.items
            )
                ? response.items.map(
                    normaliseComment
                )
                : [];
        };

    /* =======================================================
       TOGGLE COMMENTS
    ======================================================= */

    const toggleComments =
        async (
            post
        ) => {
            if (
                post._showComments
            ) {
                setPosts(
                    (previous) =>
                        previous.map(
                            (item) =>
                                item.id ===
                                    post.id
                                    ? {
                                        ...item,

                                        _showComments:
                                            false,
                                    }
                                    : item
                        )
                );

                return;
            }

            try {
                setError("");

                const comments =
                    await loadComments(
                        post.id
                    );

                setPosts(
                    (previous) =>
                        previous.map(
                            (item) =>
                                item.id ===
                                    post.id
                                    ? {
                                        ...item,

                                        _showComments:
                                            true,

                                        _comments:
                                            comments,
                                    }
                                    : item
                        )
                );

            } catch (err) {
                console.error(
                    "Comments failed:",
                    err
                );

                setError(
                    err?.message ||
                    "We couldn't load the comments."
                );
            }
        };

    /* =======================================================
       ADD COMMENT
    ======================================================= */

    const addComment =
        async (
            post,
            bodyText
        ) => {
            const body =
                (
                    bodyText ||
                    ""
                ).trim();

            if (!body) {
                return;
            }

            try {
                setError("");

                const response =
                    await FeedAPI
                        .commentOnPost({
                            postId:
                                post.id,

                            userId:
                                actorShopId,

                            userName:
                                actorName,

                            userEmail:
                                authenticatedEmail,

                            shopId:
                                actorShopId,

                            text:
                                body,
                        });

                const newComment =
                    response?.comment
                        ? normaliseComment(
                            response.comment
                        )
                        : null;

                if (
                    !newComment
                ) {
                    const comments =
                        await loadComments(
                            post.id
                        );

                    setPosts(
                        (previous) =>
                            previous.map(
                                (item) =>
                                    item.id ===
                                        post.id
                                        ? {
                                            ...item,

                                            _comments:
                                                comments,

                                            commentCount:
                                                Math.max(
                                                    Number(
                                                        item.commentCount ||
                                                        0
                                                    ) +
                                                    1,

                                                    comments.length
                                                ),
                                        }
                                        : item
                            )
                    );

                    return;
                }

                setPosts(
                    (previous) =>
                        previous.map(
                            (item) =>
                                item.id ===
                                    post.id
                                    ? {
                                        ...item,

                                        _comments: [
                                            newComment,
                                            ...(
                                                item._comments ||
                                                []
                                            ),
                                        ],

                                        commentCount:
                                            Number(
                                                item.commentCount ||
                                                0
                                            ) +
                                            1,
                                    }
                                    : item
                        )
                );

            } catch (err) {
                console.error(
                    "Comment failed:",
                    err
                );

                setError(
                    err?.message ||
                    "We couldn't add your comment."
                );
            }
        };

    /* =======================================================
       LIKE COMMENT
    ======================================================= */

    const toggleLikeComment =
        async (
            postId,
            comment
        ) => {
            const originalLiked =
                !!comment.likedByMe;

            const originalCount =
                Number(
                    comment.likeCount ||
                    0
                );

            const nextLiked =
                !originalLiked;

            const nextCount =
                Math.max(
                    0,
                    originalCount +
                    (
                        nextLiked
                            ? 1
                            : -1
                    )
                );

            const updateComment =
                (
                    liked,
                    count
                ) => {
                    setPosts(
                        (previous) =>
                            previous.map(
                                (post) =>
                                    post.id !==
                                        postId
                                        ? post
                                        : {
                                            ...post,

                                            _comments:
                                                (
                                                    post._comments ||
                                                    []
                                                ).map(
                                                    (item) =>
                                                        item.id ===
                                                            comment.id
                                                            ? {
                                                                ...item,

                                                                likedByMe:
                                                                    liked,

                                                                likeCount:
                                                                    count,
                                                            }
                                                            : item
                                                ),
                                        }
                            )
                    );
                };

            updateComment(
                nextLiked,
                nextCount
            );

            try {
                await FeedAPI
                    .likeFeedPost({
                        postId,

                        userId:
                            actorShopId,

                        userName:
                            actorName,

                        userEmail:
                            authenticatedEmail,

                        shopId:
                            actorShopId,

                        commentId:
                            comment.id,

                        target:
                            "comment",
                    });

            } catch (err) {
                updateComment(
                    originalLiked,
                    originalCount
                );

                setError(
                    err?.message ||
                    "We couldn't update that comment like."
                );
            }
        };

    /* =======================================================
       EDIT COMMENT
    ======================================================= */

    const startEditComment =
        (
            postId,
            commentId
        ) => {
            setPosts(
                (previous) =>
                    previous.map(
                        (post) =>
                            post.id !==
                                postId
                                ? post
                                : {
                                    ...post,

                                    _comments:
                                        (
                                            post._comments ||
                                            []
                                        ).map(
                                            (comment) =>
                                                comment.id ===
                                                    commentId
                                                    ? {
                                                        ...comment,

                                                        _editing:
                                                            true,

                                                        _editText:
                                                            comment.text ||
                                                            "",
                                                    }
                                                    : comment
                                        ),
                                }
                    )
            );
        };

    const cancelEditComment =
        (
            postId,
            commentId
        ) => {
            setPosts(
                (previous) =>
                    previous.map(
                        (post) =>
                            post.id !==
                                postId
                                ? post
                                : {
                                    ...post,

                                    _comments:
                                        (
                                            post._comments ||
                                            []
                                        ).map(
                                            (comment) =>
                                                comment.id ===
                                                    commentId
                                                    ? {
                                                        ...comment,

                                                        _editing:
                                                            false,

                                                        _editText:
                                                            "",
                                                    }
                                                    : comment
                                        ),
                                }
                    )
            );
        };

    const updateCommentEditText =
        (
            postId,
            commentId,
            value
        ) => {
            setPosts(
                (previous) =>
                    previous.map(
                        (post) =>
                            post.id !==
                                postId
                                ? post
                                : {
                                    ...post,

                                    _comments:
                                        (
                                            post._comments ||
                                            []
                                        ).map(
                                            (comment) =>
                                                comment.id ===
                                                    commentId
                                                    ? {
                                                        ...comment,

                                                        _editText:
                                                            value,
                                                    }
                                                    : comment
                                        ),
                                }
                    )
            );
        };

    const saveEditComment =
        async (
            postId,
            comment
        ) => {
            const cleanText =
                (
                    comment._editText ||
                    ""
                ).trim();

            if (!cleanText) {
                return;
            }

            try {
                setError("");

                await FeedAPI
                    .manageComment({
                        action:
                            "edit",

                        postId,

                        commentId:
                            comment.id,

                        userId:
                            actorShopId,

                        userEmail:
                            authenticatedEmail,

                        shopId:
                            actorShopId,

                        text:
                            cleanText,
                    });

                const comments =
                    await loadComments(
                        postId
                    );

                setPosts(
                    (previous) =>
                        previous.map(
                            (post) =>
                                post.id ===
                                    postId
                                    ? {
                                        ...post,

                                        _comments:
                                            comments,
                                    }
                                    : post
                        )
                );

            } catch (err) {
                console.error(
                    "Edit comment failed:",
                    err
                );

                setError(
                    err?.message ||
                    "We couldn't update your comment."
                );
            }
        };

    /* =======================================================
       DELETE COMMENT
    ======================================================= */

    const deleteComment =
        async (
            postId,
            commentId
        ) => {
            const confirmed =
                window.confirm(
                    "Delete this comment?"
                );

            if (!confirmed) {
                return;
            }

            try {
                setError("");

                await FeedAPI
                    .manageComment({
                        action:
                            "delete",

                        postId,

                        commentId,

                        userId:
                            actorShopId,

                        userEmail:
                            authenticatedEmail,

                        shopId:
                            actorShopId,
                    });

                const comments =
                    await loadComments(
                        postId
                    );

                setPosts(
                    (previous) =>
                        previous.map(
                            (post) =>
                                post.id ===
                                    postId
                                    ? {
                                        ...post,

                                        _comments:
                                            comments,

                                        commentCount:
                                            Math.max(
                                                Number(
                                                    post.commentCount ||
                                                    1
                                                ) -
                                                1,

                                                0
                                            ),
                                    }
                                    : post
                        )
                );

            } catch (err) {
                console.error(
                    "Delete comment failed:",
                    err
                );

                setError(
                    err?.message ||
                    "We couldn't delete that comment."
                );
            }
        };

    /* =======================================================
       DEEP LINK
    ======================================================= */

    const ensureOpenAndScroll =
        useCallback(
            async (
                postId,
                commentId = null
            ) => {
                if (!postId) {
                    return;
                }

                let availablePosts =
                    posts;

                let post =
                    availablePosts.find(
                        (item) =>
                            item.id ===
                            postId
                    );

                if (!post) {
                    availablePosts =
                        await refreshFeed();

                    post =
                        availablePosts.find(
                            (item) =>
                                item.id ===
                                postId
                        );
                }

                if (!post) {
                    setError(
                        "That Community Feed post could not be found."
                    );

                    return;
                }

                try {
                    const comments =
                        await loadComments(
                            postId,
                            50
                        );

                    setPosts(
                        (previous) =>
                            previous.map(
                                (item) =>
                                    item.id ===
                                        postId
                                        ? {
                                            ...item,

                                            _showComments:
                                                true,

                                            _comments:
                                                comments,
                                        }
                                        : item
                            )
                    );

                } catch (err) {
                    console.warn(
                        "Deep-link comments failed:",
                        err
                    );
                }

                setTimeout(
                    () => {
                        const postElement =
                            document.getElementById(
                                `post-${postId}`
                            );

                        postElement
                            ?.scrollIntoView({
                                behavior:
                                    "smooth",

                                block:
                                    "center",
                            });

                        if (
                            commentId
                        ) {
                            setTimeout(
                                () => {
                                    const commentElement =
                                        document.getElementById(
                                            `comment-${commentId}`
                                        );

                                    commentElement
                                        ?.scrollIntoView({
                                            behavior:
                                                "smooth",

                                            block:
                                                "center",
                                        });
                                },
                                150
                            );
                        }

                        setHighlightPostId(
                            postId
                        );

                        setHighlightCommentId(
                            commentId
                        );

                        setTimeout(
                            () => {
                                setHighlightPostId(
                                    null
                                );

                                setHighlightCommentId(
                                    null
                                );
                            },
                            2500
                        );
                    },
                    120
                );
            },
            [
                posts,
                refreshFeed,
            ]
        );

    useEffect(() => {
        if (
            !initialLoading &&
            targetPostId
        ) {
            ensureOpenAndScroll(
                targetPostId,
                targetCommentId
            );
        }
    }, [
        initialLoading,
        targetPostId,
        targetCommentId,
        ensureOpenAndScroll,
    ]);

    /* =======================================================
       LOCAL STORAGE DEEP LINK FALLBACK
    ======================================================= */

    useEffect(() => {
        if (
            initialLoading ||
            targetPostId
        ) {
            return;
        }

        try {
            const stored =
                localStorage.getItem(
                    "feed_deeplink"
                );

            if (!stored) {
                return;
            }

            const deeplink =
                JSON.parse(
                    stored
                );

            localStorage.removeItem(
                "feed_deeplink"
            );

            if (
                deeplink?.postId
            ) {
                ensureOpenAndScroll(
                    deeplink.postId,

                    deeplink.commentId ||
                    deeplink.highlightCommentId ||
                    null
                );
            }

        } catch (err) {
            console.warn(
                "Invalid feed_deeplink:",
                err
            );

            localStorage.removeItem(
                "feed_deeplink"
            );
        }

    }, [
        initialLoading,
        targetPostId,
        ensureOpenAndScroll,
    ]);

    /* =======================================================
       COMMENT COMPOSER
    ======================================================= */

    const AddCommentBox = ({
        onSubmit,
    }) => {
        const [
            value,
            setValue,
        ] = useState("");

        const submit =
            () => {
                const clean =
                    value.trim();

                if (!clean) {
                    return;
                }

                onSubmit(
                    clean
                );

                setValue("");
            };

        return (
            <div className="feed-comment-composer">

                <div className="feed-comment-avatar">
                    {currentName
                        .slice(
                            0,
                            1
                        )
                        .toUpperCase()}
                </div>

                <div className="feed-comment-input-wrap">

                    <input
                        value={
                            value
                        }
                        onChange={
                            (event) =>
                                setValue(
                                    event
                                        .target
                                        .value
                                )
                        }
                        placeholder="Write a comment..."
                        onKeyDown={
                            (event) => {
                                if (
                                    event.key ===
                                    "Enter" &&
                                    !event.shiftKey
                                ) {
                                    event.preventDefault();

                                    submit();
                                }
                            }
                        }
                    />

                    <button
                        type="button"
                        onClick={
                            submit
                        }
                        disabled={
                            !value.trim()
                        }
                        aria-label="Post comment"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="15"
                            height="15"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="m22 2-7 20-4-9-9-4Z" />

                            <path d="M22 2 11 13" />
                        </svg>
                    </button>

                </div>

            </div>
        );
    };

    /* =======================================================
       POST CARD
    ======================================================= */

    const PostCard = ({
        post,
    }) => {
        const mine =
            (
                actorShopId &&
                (
                    post.shopId ===
                    actorShopId ||
                    post.authorId ===
                    actorShopId
                )
            ) ||
            (
                actorEmail &&
                (
                    String(
                        post.authorEmail ||
                        ""
                    )
                        .trim()
                        .toLowerCase() ===
                    actorEmail ||

                    String(
                        post.authorId ||
                        ""
                    )
                        .trim()
                        .toLowerCase() ===
                    actorEmail
                )
            );

        const highlighted =
            highlightPostId ===
            post.id;

        const authorInitial =
            (
                post.authorName ||
                "O"
            )
                .slice(
                    0,
                    1
                )
                .toUpperCase();

        return (
            <article
                id={`post-${post.id}`}
                className={`feed-post-card ${highlighted
                        ? "feed-post-highlight"
                        : ""
                    }`}
            >

                {/* AUTHOR */}

                <div className="feed-post-header">

                    <div className="feed-author">

                        <div className="feed-avatar">
                            {authorInitial}
                        </div>

                        <div>

                            <div className="feed-author-line">

                                <strong>
                                    {post.authorName}
                                </strong>

                                {post.authorType ===
                                    "opshop" && (
                                        <span className="feed-role-badge">
                                            Op Shop
                                        </span>
                                    )}

                            </div>

                            <span className="feed-post-time">
                                {relativeTime(
                                    post.createdAt
                                )}
                            </span>

                        </div>

                    </div>

                    {mine && (
                        <details className="feed-menu">

                            <summary aria-label="Post options">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="18"
                                    height="18"
                                    fill="currentColor"
                                >
                                    <circle
                                        cx="5"
                                        cy="12"
                                        r="1.6"
                                    />

                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="1.6"
                                    />

                                    <circle
                                        cx="19"
                                        cy="12"
                                        r="1.6"
                                    />
                                </svg>
                            </summary>

                            <div className="feed-menu-popover">

                                {editingPostId ===
                                    post.id ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={
                                                saveEdit
                                            }
                                        >
                                            Save changes
                                        </button>

                                        <button
                                            type="button"
                                            onClick={
                                                cancelEdit
                                            }
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                startEditPost(
                                                    post
                                                )
                                            }
                                        >
                                            Edit post
                                        </button>

                                        <button
                                            type="button"
                                            className="feed-menu-delete"
                                            onClick={() =>
                                                deletePost(
                                                    post.id
                                                )
                                            }
                                        >
                                            Delete post
                                        </button>
                                    </>
                                )}

                            </div>

                        </details>
                    )}

                </div>

                {/* BODY */}

                {editingPostId ===
                    post.id ? (
                    <div className="feed-edit-wrap">

                        <textarea
                            value={
                                editingText
                            }
                            onChange={
                                (event) =>
                                    setEditingText(
                                        event
                                            .target
                                            .value
                                            .slice(
                                                0,
                                                MAX_POST_LENGTH
                                            )
                                    )
                            }
                            rows={4}
                        />

                        <div className="feed-edit-footer">

                            <span>
                                {editingText.length}
                                /{MAX_POST_LENGTH}
                            </span>

                            <div>
                                <button
                                    type="button"
                                    className="feed-secondary-button"
                                    onClick={
                                        cancelEdit
                                    }
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="feed-primary-small-button"
                                    onClick={
                                        saveEdit
                                    }
                                >
                                    Save
                                </button>
                            </div>

                        </div>

                    </div>
                ) : post.text ? (
                    <div className="feed-post-text">
                        {post.text}
                    </div>
                ) : null}

                {/* MULTI PHOTO CAROUSEL */}

                <PostMedia
                    post={
                        post
                    }
                />

                {/* COUNTS */}

                {(
                    post.likeCount >
                    0 ||
                    post.commentCount >
                    0
                ) && (
                        <div className="feed-count-row">

                            <span>
                                {post.likeCount >
                                    0
                                    ? `${post.likeCount} ${post.likeCount ===
                                        1
                                        ? "like"
                                        : "likes"
                                    }`
                                    : ""}
                            </span>

                            <span>
                                {post.commentCount >
                                    0
                                    ? `${post.commentCount} ${post.commentCount ===
                                        1
                                        ? "comment"
                                        : "comments"
                                    }`
                                    : ""}
                            </span>

                        </div>
                    )}

                {/* ACTIONS */}

                <div className="feed-actions">

                    <button
                        type="button"
                        className={
                            post.likedByMe
                                ? "feed-action-active"
                                : ""
                        }
                        onClick={() =>
                            toggleLikePost(
                                post
                            )
                        }
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill={
                                post.likedByMe
                                    ? "currentColor"
                                    : "none"
                            }
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M7 10v11H3V10h4Z" />

                            <path d="M7 19h10.3a2 2 0 0 0 1.9-1.4l1.6-5a2 2 0 0 0-1.9-2.6H14l.7-3.4A3 3 0 0 0 11.8 3L7 10Z" />
                        </svg>

                        Like
                    </button>

                    <button
                        type="button"
                        className={
                            post._showComments
                                ? "feed-action-active"
                                : ""
                        }
                        onClick={() =>
                            toggleComments(
                                post
                            )
                        }
                    >
                        <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
                        </svg>

                        Comment
                    </button>

                </div>

                {/* COMMENTS */}

                {post._showComments && (
                    <div className="feed-comments">

                        <AddCommentBox
                            onSubmit={
                                (value) =>
                                    addComment(
                                        post,
                                        value
                                    )
                            }
                        />

                        {(
                            post._comments ||
                            []
                        ).length ===
                            0 ? (
                            <div className="feed-no-comments">
                                No comments yet. Be the first to join the conversation.
                            </div>
                        ) : (
                            <div className="feed-comment-list">

                                {(
                                    post._comments ||
                                    []
                                ).map(
                                    (comment) => {
                                        const commentMine =
                                            (
                                                actorShopId &&
                                                (
                                                    comment.shopId ===
                                                    actorShopId ||

                                                    comment.userId ===
                                                    actorShopId ||

                                                    comment.authorId ===
                                                    actorShopId
                                                )
                                            ) ||
                                            (
                                                actorEmail &&
                                                (
                                                    String(
                                                        comment.userEmail ||
                                                        comment.authorEmail ||
                                                        ""
                                                    )
                                                        .trim()
                                                        .toLowerCase() ===
                                                    actorEmail ||

                                                    String(
                                                        comment.userId ||
                                                        comment.authorId ||
                                                        ""
                                                    )
                                                        .trim()
                                                        .toLowerCase() ===
                                                    actorEmail
                                                )
                                            );

                                        const commentHighlighted =
                                            highlightCommentId ===
                                            comment.id;

                                        const commentInitial =
                                            (
                                                comment.userName ||
                                                "O"
                                            )
                                                .slice(
                                                    0,
                                                    1
                                                )
                                                .toUpperCase();

                                        return (
                                            <div
                                                key={
                                                    comment.id
                                                }
                                                id={`comment-${comment.id}`}
                                                className={`feed-comment ${commentHighlighted
                                                        ? "feed-comment-highlight"
                                                        : ""
                                                    }`}
                                            >

                                                <div className="feed-comment-avatar">
                                                    {commentInitial}
                                                </div>

                                                <div className="feed-comment-content">

                                                    <div className="feed-comment-bubble">

                                                        <div className="feed-comment-meta">

                                                            <strong>
                                                                {comment.userName}
                                                            </strong>

                                                            <span>
                                                                {relativeTime(
                                                                    comment.createdAt
                                                                )}
                                                            </span>

                                                        </div>

                                                        {comment._editing ? (
                                                            <textarea
                                                                value={
                                                                    comment._editText ||
                                                                    ""
                                                                }
                                                                onChange={
                                                                    (event) =>
                                                                        updateCommentEditText(
                                                                            post.id,
                                                                            comment.id,
                                                                            event.target.value
                                                                        )
                                                                }
                                                                rows={2}
                                                            />
                                                        ) : (
                                                            <div className="feed-comment-text">
                                                                {comment.text}
                                                            </div>
                                                        )}

                                                    </div>

                                                    <div className="feed-comment-actions">

                                                        <button
                                                            type="button"
                                                            className={
                                                                comment.likedByMe
                                                                    ? "active"
                                                                    : ""
                                                            }
                                                            onClick={() =>
                                                                toggleLikeComment(
                                                                    post.id,
                                                                    comment
                                                                )
                                                            }
                                                        >
                                                            Like
                                                            {comment.likeCount >
                                                                0
                                                                ? ` · ${comment.likeCount}`
                                                                : ""}
                                                        </button>

                                                        {commentMine &&
                                                            !comment._editing && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            startEditComment(
                                                                                post.id,
                                                                                comment.id
                                                                            )
                                                                        }
                                                                    >
                                                                        Edit
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="delete"
                                                                        onClick={() =>
                                                                            deleteComment(
                                                                                post.id,
                                                                                comment.id
                                                                            )
                                                                        }
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </>
                                                            )}

                                                        {comment._editing && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        saveEditComment(
                                                                            post.id,
                                                                            comment
                                                                        )
                                                                    }
                                                                >
                                                                    Save
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        cancelEditComment(
                                                                            post.id,
                                                                            comment.id
                                                                        )
                                                                    }
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </>
                                                        )}

                                                    </div>

                                                </div>

                                            </div>
                                        );
                                    }
                                )}

                            </div>
                        )}

                    </div>
                )}

            </article>
        );
    };

    /* =======================================================
       RENDER
    ======================================================= */

    return (
        <>
            <style>
                {communityStyles}
            </style>

            <div className="community-page">

                {/* INTRO */}

                <section className="community-intro">

                    <div>

                        <div className="community-eyebrow">
                            OPPIES Community
                        </div>

                        <h1>
                            Community Feed
                        </h1>

                        <p>
                            Share shop updates, thrifting finds and news with the OPPIES community.
                        </p>

                    </div>

                    <button
                        type="button"
                        className="community-refresh"
                        onClick={() =>
                            refreshFeed({
                                showLoader:
                                    true,
                            })
                        }
                        disabled={
                            refreshing
                        }
                    >
                        <svg
                            className={
                                refreshing
                                    ? "community-spin"
                                    : ""
                            }
                            viewBox="0 0 24 24"
                            width="15"
                            height="15"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.9"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M20 6v5h-5" />
                            <path d="M4 18v-5h5" />
                            <path d="M18.5 9A7 7 0 0 0 6.7 6.5L4 9" />
                            <path d="M5.5 15a7 7 0 0 0 11.8 2.5L20 15" />
                        </svg>

                        {refreshing
                            ? "Refreshing..."
                            : "Refresh"}
                    </button>

                </section>

                {/* ERROR */}

                {error && (
                    <div className="community-error">

                        <div className="community-error-icon">
                            !
                        </div>

                        <span>
                            {error}
                        </span>

                        <button
                            type="button"
                            onClick={() =>
                                refreshFeed({
                                    showLoader:
                                        true,
                                })
                            }
                        >
                            Retry
                        </button>

                    </div>
                )}

                {/* COMPOSER */}

                <section className="community-composer">

                    <div className="community-composer-header">

                        <div className="community-composer-avatar">
                            {currentName
                                .slice(
                                    0,
                                    1
                                )
                                .toUpperCase()}
                        </div>

                        <div>
                            <strong>
                                Share with the community
                            </strong>

                            <span>
                                Posting as{" "}
                                {currentName}
                            </span>
                        </div>

                    </div>

                    <textarea
                        value={
                            text
                        }
                        onChange={
                            (event) =>
                                setText(
                                    event
                                        .target
                                        .value
                                        .slice(
                                            0,
                                            MAX_POST_LENGTH
                                        )
                                )
                        }
                        rows={4}
                        placeholder="Share an update, a thrifting find, shop news or something with the OPPIES community..."
                        disabled={
                            posting
                        }
                    />

                    {/* MULTI IMAGE PREVIEWS */}

                    {files.length >
                        0 && (
                            <div className="community-image-previews">

                                <div className="community-preview-grid">

                                    {files.map(
                                        (
                                            item,
                                            index
                                        ) => (
                                            <div
                                                key={
                                                    item.id
                                                }
                                                className="community-image-preview"
                                            >

                                                <img
                                                    src={
                                                        item.preview
                                                    }
                                                    alt={`Post preview ${index + 1}`}
                                                />

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        removeFile(
                                                            item.id
                                                        )
                                                    }
                                                    disabled={
                                                        posting
                                                    }
                                                    aria-label={`Remove ${item.file?.name || `photo ${index + 1}`}`}
                                                >
                                                    <svg
                                                        viewBox="0 0 24 24"
                                                        width="15"
                                                        height="15"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    >
                                                        <path d="M18 6 6 18" />
                                                        <path d="m6 6 12 12" />
                                                    </svg>
                                                </button>

                                                <div className="community-preview-name">
                                                    {item.file?.name}
                                                </div>

                                            </div>
                                        )
                                    )}

                                </div>

                                <div className="community-preview-summary">

                                    <span>
                                        {files.length}
                                        {" of "}
                                        {MAX_IMAGES}
                                        {" photos selected"}
                                    </span>

                                    <button
                                        type="button"
                                        onClick={
                                            clearFiles
                                        }
                                        disabled={
                                            posting
                                        }
                                    >
                                        Remove all
                                    </button>

                                </div>

                            </div>
                        )}

                    <div className="community-composer-footer">

                        <div className="community-composer-tools">

                            <input
                                ref={
                                    fileRef
                                }
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={
                                    (event) =>
                                        handleFiles(
                                            event
                                                .target
                                                .files
                                        )
                                }
                            />

                            <button
                                type="button"
                                className="community-photo-button"
                                onClick={() =>
                                    fileRef.current
                                        ?.click()
                                }
                                disabled={
                                    posting ||
                                    files.length >=
                                    MAX_IMAGES
                                }
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <rect
                                        x="3"
                                        y="4"
                                        width="18"
                                        height="16"
                                        rx="3"
                                    />

                                    <circle
                                        cx="9"
                                        cy="10"
                                        r="2"
                                    />

                                    <path d="m21 15-5-5L5 20" />
                                </svg>

                                {files.length >
                                    0
                                    ? files.length >=
                                        MAX_IMAGES
                                        ? "Photo Limit Reached"
                                        : "Add More Photos"
                                    : "Add Photos"}

                            </button>

                            <span
                                className={`community-character-count ${charactersLeft <
                                        50
                                        ? "warning"
                                        : ""
                                    }`}
                            >
                                {text.length}/
                                {MAX_POST_LENGTH}
                            </span>

                        </div>

                        <button
                            type="button"
                            className="community-post-button"
                            onClick={
                                handleSubmit
                            }
                            disabled={
                                !canPost
                            }
                        >
                            {posting ? (
                                <>
                                    <span className="community-button-spinner" />

                                    Posting...
                                </>
                            ) : (
                                <>
                                    Post

                                    <svg
                                        viewBox="0 0 24 24"
                                        width="14"
                                        height="14"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="m22 2-7 20-4-9-9-4Z" />
                                        <path d="M22 2 11 13" />
                                    </svg>
                                </>
                            )}

                        </button>

                    </div>

                </section>

                {/* FEED HEADER */}

                <div className="community-feed-heading">

                    <div>
                        <div className="community-feed-eyebrow">
                            Latest
                        </div>

                        <h2>
                            Community posts
                        </h2>
                    </div>

                    {posts.length >
                        0 && (
                            <span>
                                {posts.length}
                                {" loaded"}
                            </span>
                        )}

                </div>

                {/* FEED */}

                {initialLoading ? (
                    <div className="community-loading">

                        <div className="community-loader" />

                        <strong>
                            Loading the Community Feed
                        </strong>

                        <span>
                            Getting the latest OPPIES posts...
                        </span>

                    </div>
                ) : posts.length ===
                    0 ? (
                    <div className="community-empty">

                        <div className="community-empty-icon">

                            <svg
                                viewBox="0 0 24 24"
                                width="28"
                                height="28"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />
                            </svg>

                        </div>

                        <strong>
                            No posts yet
                        </strong>

                        <span>
                            Be the first to share something with the OPPIES community.
                        </span>

                    </div>
                ) : (
                    <div className="community-feed">

                        {posts.map(
                            (post) => (
                                <PostCard
                                    key={
                                        post.id
                                    }
                                    post={
                                        post
                                    }
                                />
                            )
                        )}

                    </div>
                )}

                {/* LOAD MORE */}

                {cursor && (
                    <div className="community-load-more-wrap">

                        <button
                            type="button"
                            className="community-load-more"
                            onClick={
                                loadMore
                            }
                            disabled={
                                loadingMore
                            }
                        >
                            {loadingMore ? (
                                <>
                                    <span className="community-small-spinner" />

                                    Loading...
                                </>
                            ) : (
                                <>
                                    Load More Posts

                                    <svg
                                        viewBox="0 0 24 24"
                                        width="13"
                                        height="13"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="m6 9 6 6 6-6" />
                                    </svg>
                                </>
                            )}

                        </button>

                    </div>
                )}

            </div>
        </>
    );
}

/* =========================================================
   STYLES
========================================================= */

const communityStyles = `

.community-page {
    max-width: 920px;
    margin: 0 auto;
    color: #1f2937;

    font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
}

/* ======================================================
   INTRO
====================================================== */

.community-intro {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 22px;
}

.community-eyebrow,
.community-feed-eyebrow {
    color: #047857;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.community-intro h1 {
    margin: 7px 0;
    color: #17211c;

    font-size:
        clamp(
            27px,
            3vw,
            37px
        );

    line-height: 1.08;
    letter-spacing: -0.035em;
}

.community-intro p {
    max-width: 620px;
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.65;
}

.community-refresh {
    display: inline-flex;
    min-height: 37px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 8px 12px;

    border: 1px solid #d4dfda;
    border-radius: 10px;

    background: #ffffff;
    color: #047857;

    cursor: pointer;

    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
}

.community-refresh:hover:not(:disabled) {
    background: #f0fdf4;
}

/* ======================================================
   ERROR
====================================================== */

.community-error {
    display: flex;
    align-items: center;
    gap: 9px;

    margin-bottom: 14px;
    padding: 10px 12px;

    border: 1px solid #fecaca;
    border-radius: 12px;

    background: #fef2f2;
    color: #b91c1c;

    font-size: 9px;
    font-weight: 600;
}

.community-error-icon {
    display: grid;
    width: 23px;
    height: 23px;
    flex: 0 0 auto;
    place-items: center;

    border-radius: 50%;

    background: #fee2e2;

    font-weight: 900;
}

.community-error span {
    flex: 1;
}

.community-error button {
    padding: 6px 9px;

    border: 1px solid #efb7b7;
    border-radius: 7px;

    background: #ffffff;
    color: #b91c1c;

    cursor: pointer;

    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
}

/* ======================================================
   COMPOSER
====================================================== */

.community-composer {
    margin-bottom: 25px;
    padding: 17px;

    border: 1px solid #eadfc9;
    border-radius: 18px;

    background: #fbf7ed;

    box-shadow:
        0 6px 20px
        rgba(
            15,
            23,
            42,
            0.035
        );
}

.community-composer-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
}

.community-composer-avatar,
.feed-avatar {
    display: grid;
    flex: 0 0 auto;
    place-items: center;

    border-radius: 50%;

    background: #047857;
    color: #ffffff;

    font-weight: 800;
}

.community-composer-avatar {
    width: 36px;
    height: 36px;
    font-size: 12px;
}

.community-composer-header strong {
    display: block;

    color: #334155;

    font-size: 10px;
}

.community-composer-header span {
    display: block;
    margin-top: 2px;

    color: #94a3b8;

    font-size: 8px;
}

.community-composer > textarea {
    box-sizing: border-box;
    width: 100%;
    min-height: 90px;

    padding: 12px;

    border: 1px solid #e1e7e3;
    border-radius: 12px;

    outline: none;
    resize: vertical;

    background: #ffffff;
    color: #334155;

    font-family: inherit;
    font-size: 12px;
    line-height: 1.55;

    transition:
        border-color 0.18s ease,
        box-shadow 0.18s ease;
}

.community-composer > textarea:focus {
    border-color: #62af92;

    box-shadow:
        0 0 0 3px
        rgba(
            4,
            120,
            87,
            0.07
        );
}

.community-composer > textarea::placeholder {
    color: #a5afa9;
}

.community-composer-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    margin-top: 10px;
}

.community-composer-tools {
    display: flex;
    align-items: center;
    gap: 10px;
}

.community-photo-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    min-height: 33px;
    padding: 7px 9px;

    border: 1px solid #dfcfaf;
    border-radius: 9px;

    background: #f8f0df;
    color: #5c4a3b;

    cursor: pointer;

    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
}

.community-photo-button:hover:not(:disabled) {
    background: #f0fdf4;
}

.community-character-count {
    color: #94a3b8;

    font-size: 8px;
    font-weight: 700;
}

.community-character-count.warning {
    color: #c27a14;
}

.community-post-button {
    display: inline-flex;
    min-width: 89px;
    min-height: 36px;

    align-items: center;
    justify-content: center;
    gap: 6px;

    padding: 8px 13px;

    border: none;
    border-radius: 9px;

    background: #047857;
    color: #ffffff;

    cursor: pointer;

    font-family: inherit;
    font-size: 9px;
    font-weight: 800;

    box-shadow:
        0 6px 15px
        rgba(
            4,
            120,
            87,
            0.14
        );
}

.community-post-button:hover:not(:disabled) {
    background: #036b4f;
}

.community-post-button:disabled,
.community-photo-button:disabled,
.community-refresh:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

/* ======================================================
   MULTI PHOTO PREVIEW
====================================================== */

.community-image-previews {
    margin-top: 10px;
}

.community-preview-grid {
    display: grid;

    grid-template-columns:
        repeat(
            2,
            minmax(0, 1fr)
        );

    gap: 9px;
}

.community-image-preview {
    position: relative;
    overflow: hidden;

    border: 1px solid #e1e7e3;
    border-radius: 13px;

    background: #f8faf9;
}

.community-image-preview img {
    display: block;

    width: 100%;

    aspect-ratio: 4 / 3;

    object-fit: cover;
}

.community-image-preview > button {
    position: absolute;
    top: 8px;
    right: 8px;

    display: grid;
    width: 28px;
    height: 28px;

    place-items: center;

    border: none;
    border-radius: 50%;

    background:
        rgba(
            20,
            30,
            25,
            0.78
        );

    color: #ffffff;

    cursor: pointer;
}

.community-image-preview > button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.community-preview-name {
    overflow: hidden;

    padding: 7px 9px;

    color: #64748b;

    font-size: 8px;

    text-overflow: ellipsis;
    white-space: nowrap;
}

.community-preview-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    margin-top: 7px;

    color: #7c8781;

    font-size: 8px;
    font-weight: 700;
}

.community-preview-summary button {
    padding: 0;

    border: none;

    background: transparent;
    color: #a45151;

    cursor: pointer;

    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
}

/* ======================================================
   FEED HEADER
====================================================== */

.community-feed-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 14px;

    margin-bottom: 11px;
}

.community-feed-heading h2 {
    margin: 4px 0 0;

    color: #17211c;

    font-size: 19px;
    letter-spacing: -0.025em;
}

.community-feed-heading > span {
    color: #94a3b8;

    font-size: 8px;
    font-weight: 700;
}

/* ======================================================
   FEED / POST CARD
====================================================== */

.community-feed {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.feed-post-card {
    scroll-margin-top: 30px;

    overflow: visible;

    padding: 17px;

    border: 1px solid #e5eae7;
    border-radius: 18px;

    background: #ffffff;

    box-shadow:
        0 5px 18px
        rgba(
            15,
            23,
            42,
            0.03
        );

    transition:
        border-color 0.2s ease,
        box-shadow 0.2s ease;
}

.feed-post-highlight {
    border-color: #28a274;

    box-shadow:
        0 0 0 3px
        rgba(
            4,
            120,
            87,
            0.09
        );
}

.feed-post-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.feed-author {
    display: flex;
    align-items: center;
    gap: 10px;
}

.feed-avatar {
    width: 38px;
    height: 38px;

    font-size: 12px;
}

.feed-author-line {
    display: flex;
    align-items: center;
    gap: 7px;
}

.feed-author-line strong {
    color: #26362e;

    font-size: 10px;
}

.feed-role-badge {
    padding: 3px 6px;

    border-radius: 999px;

    background: #ecfdf5;
    color: #047857;

    font-size: 6px;
    font-weight: 900;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.feed-post-time {
    display: block;

    margin-top: 2px;

    color: #94a3b8;

    font-size: 8px;
}

/* ======================================================
   MENU
====================================================== */

.feed-menu {
    position: relative;
}

.feed-menu summary {
    display: grid;

    width: 30px;
    height: 30px;

    place-items: center;

    border-radius: 8px;

    color: #7f8d86;

    cursor: pointer;
    list-style: none;
}

.feed-menu summary::-webkit-details-marker {
    display: none;
}

.feed-menu summary:hover {
    background: #f3f6f4;
}

.feed-menu-popover {
    position: absolute;

    top: 35px;
    right: 0;
    z-index: 30;

    display: flex;
    min-width: 135px;

    flex-direction: column;
    gap: 2px;

    padding: 6px;

    border: 1px solid #e0e6e2;
    border-radius: 10px;

    background: #ffffff;

    box-shadow:
        0 10px 28px
        rgba(
            15,
            23,
            42,
            0.12
        );
}

.feed-menu-popover button {
    padding: 7px 8px;

    border: none;
    border-radius: 6px;

    background: transparent;
    color: #475569;

    cursor: pointer;

    font-family: inherit;
    font-size: 8px;
    font-weight: 700;
    text-align: left;
}

.feed-menu-popover button:hover {
    background: #f6f8f7;
}

.feed-menu-popover .feed-menu-delete {
    color: #b91c1c;
}

/* ======================================================
   POST TEXT
====================================================== */

.feed-post-text {
    margin: 13px 0 10px;

    color: #334155;

    font-size: 12px;
    line-height: 1.6;

    white-space: pre-wrap;
    word-break: break-word;
}

/* ======================================================
   MEDIA CAROUSEL
====================================================== */

.feed-post-media {
    margin-top: 11px;
}

.feed-media-stage {
    position: relative;

    overflow: hidden;

    border: 1px solid #e7ebe9;
    border-radius: 14px;

    background: #f7f9f8;
}

.feed-media-stage img {
    display: block;

    width: 100%;

    max-height: 520px;

    object-fit: cover;
}

.feed-media-arrow {
    position: absolute;
    top: 50%;

    display: grid;

    width: 34px;
    height: 34px;

    transform:
        translateY(-50%);

    place-items: center;

    border: none;
    border-radius: 50%;

    background:
        rgba(
            20,
            30,
            25,
            0.72
        );

    color: #ffffff;

    cursor: pointer;

    backdrop-filter:
        blur(5px);
}

.feed-media-arrow:hover {
    background:
        rgba(
            20,
            30,
            25,
            0.86
        );
}

.feed-media-arrow-left {
    left: 10px;
}

.feed-media-arrow-right {
    right: 10px;
}

.feed-media-counter {
    position: absolute;

    top: 10px;
    right: 10px;

    padding: 5px 8px;

    border-radius: 999px;

    background:
        rgba(
            20,
            30,
            25,
            0.70
        );

    color: #ffffff;

    font-size: 8px;
    font-weight: 800;

    backdrop-filter:
        blur(5px);
}

.feed-media-dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    margin-top: 7px;
}

.feed-media-dots button {
    width: 7px;
    height: 7px;

    padding: 0;

    border: none;
    border-radius: 50%;

    background: #ccd5d0;

    cursor: pointer;
}

.feed-media-dots button.active {
    background: #047857;
}

/* ======================================================
   EDIT POST
====================================================== */

.feed-edit-wrap {
    margin-top: 12px;
}

.feed-edit-wrap textarea {
    box-sizing: border-box;
    width: 100%;

    padding: 10px;

    border: 1px solid #dce4df;
    border-radius: 10px;

    outline: none;
    resize: vertical;

    color: #334155;

    font-family: inherit;
    font-size: 11px;
}

.feed-edit-wrap textarea:focus {
    border-color: #55a989;
}

.feed-edit-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    margin-top: 7px;
}

.feed-edit-footer > span {
    color: #94a3b8;

    font-size: 7px;
}

.feed-edit-footer > div {
    display: flex;
    gap: 6px;
}

.feed-secondary-button,
.feed-primary-small-button {
    min-height: 30px;

    padding: 6px 9px;

    border-radius: 8px;

    cursor: pointer;

    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
}

.feed-secondary-button {
    border: 1px solid #d8dfdb;

    background: #ffffff;
    color: #64748b;
}

.feed-primary-small-button {
    border: none;

    background: #047857;
    color: #ffffff;
}

/* ======================================================
   COUNTS / ACTIONS
====================================================== */

.feed-count-row {
    display: flex;
    justify-content: space-between;

    min-height: 17px;

    margin-top: 9px;

    color: #94a3b8;

    font-size: 7px;
}

.feed-actions {
    display: grid;

    grid-template-columns:
        repeat(
            2,
            minmax(0, 1fr)
        );

    gap: 7px;

    margin-top: 7px;
    padding-top: 8px;

    border-top:
        1px solid #edf1ef;
}

.feed-actions button {
    display: flex;

    min-height: 34px;

    align-items: center;
    justify-content: center;

    gap: 6px;

    border: none;
    border-radius: 8px;

    background: transparent;
    color: #64748b;

    cursor: pointer;

    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
}

.feed-actions button:hover {
    background: #f4f7f5;
}

.feed-actions .feed-action-active {
    background: #ecfdf5;
    color: #047857;
}

/* ======================================================
   COMMENTS
====================================================== */

.feed-comments {
    margin-top: 8px;
    padding-top: 11px;

    border-top:
        1px solid #edf1ef;
}

.feed-comment-composer {
    display: flex;
    align-items: center;
    gap: 8px;

    margin-bottom: 12px;
}

.feed-comment-avatar {
    display: grid;

    width: 29px;
    height: 29px;

    flex: 0 0 auto;

    place-items: center;

    border-radius: 50%;

    background: #e7f3ed;
    color: #047857;

    font-size: 9px;
    font-weight: 800;
}

.feed-comment-input-wrap {
    display: flex;

    min-width: 0;

    flex: 1;

    align-items: center;

    overflow: hidden;

    border: 1px solid #e5dac4;
    border-radius: 999px;

    background: #f8faf9;
}

.feed-comment-input-wrap input {
    min-width: 0;

    flex: 1;

    padding: 8px 11px;

    border: none;
    outline: none;

    background: transparent;

    color: #334155;

    font-family: inherit;
    font-size: 9px;
}

.feed-comment-input-wrap button {
    display: grid;

    width: 33px;
    height: 33px;

    flex: 0 0 auto;

    place-items: center;

    border: none;

    background: transparent;
    color: #047857;

    cursor: pointer;
}

.feed-comment-input-wrap button:disabled {
    cursor: not-allowed;
    opacity: 0.35;
}

.feed-comment-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.feed-comment {
    display: flex;

    gap: 8px;

    padding: 3px;

    border-radius: 11px;

    transition:
        background 0.2s ease,
        box-shadow 0.2s ease;
}

.feed-comment-highlight {
    background: #ecfdf5;

    box-shadow:
        0 0 0 2px
        rgba(
            4,
            120,
            87,
            0.13
        );
}

.feed-comment-content {
    min-width: 0;
    flex: 1;
}

.feed-comment-bubble {
    display: inline-block;

    max-width: 88%;

    padding: 8px 10px;

    border-radius:
        4px 12px 12px 12px;

    background: #f7f0e3;
}

.feed-comment-meta {
    display: flex;
    align-items: center;
    gap: 6px;

    margin-bottom: 3px;
}

.feed-comment-meta strong {
    color: #38473f;

    font-size: 8px;
}

.feed-comment-meta span {
    color: #9aa5a0;

    font-size: 6px;
}

.feed-comment-text {
    color: #52605a;

    font-size: 9px;
    line-height: 1.5;

    white-space: pre-wrap;
    word-break: break-word;
}

.feed-comment-bubble textarea {
    box-sizing: border-box;

    width:
        min(
            500px,
            70vw
        );

    min-height: 52px;

    padding: 7px;

    border: 1px solid #cedbd4;
    border-radius: 7px;

    outline: none;
    resize: vertical;

    background: #ffffff;
    color: #334155;

    font-family: inherit;
    font-size: 9px;
}

.feed-comment-actions {
    display: flex;
    gap: 7px;

    padding: 4px 9px 0;
}

.feed-comment-actions button {
    padding: 0;

    border: none;

    background: transparent;
    color: #78867f;

    cursor: pointer;

    font-family: inherit;
    font-size: 7px;
    font-weight: 800;
}

.feed-comment-actions button:hover,
.feed-comment-actions button.active {
    color: #047857;
}

.feed-comment-actions button.delete {
    color: #b65a5a;
}

.feed-no-comments {
    padding:
        9px 0 3px 37px;

    color: #9aa5a0;

    font-size: 8px;
}

/* ======================================================
   LOADING / EMPTY
====================================================== */

.community-loading,
.community-empty {
    display: flex;

    min-height: 250px;

    flex-direction: column;
    align-items: center;
    justify-content: center;

    padding: 25px;

    border: 1px solid #e6ebe8;
    border-radius: 17px;

    background: #ffffff;

    text-align: center;
}

.community-loader {
    width: 29px;
    height: 29px;

    margin-bottom: 11px;

    border:
        3px solid #d5eee5;

    border-top-color:
        #047857;

    border-radius: 50%;

    animation:
        community-rotate
        0.8s
        linear
        infinite;
}

.community-loading strong,
.community-empty strong {
    color: #53615a;

    font-size: 10px;
}

.community-loading span,
.community-empty span {
    max-width: 260px;

    margin-top: 4px;

    color: #94a3b8;

    font-size: 8px;
    line-height: 1.5;
}

.community-empty-icon {
    display: grid;

    width: 52px;
    height: 52px;

    margin-bottom: 11px;

    place-items: center;

    border-radius: 15px;

    background: #eef5f1;
    color: #709486;
}

/* ======================================================
   LOAD MORE
====================================================== */

.community-load-more-wrap {
    display: flex;
    justify-content: center;

    margin-top: 17px;
}

.community-load-more {
    display: inline-flex;

    min-height: 35px;

    align-items: center;
    justify-content: center;

    gap: 6px;

    padding: 7px 12px;

    border: 1px solid #c9dcd4;
    border-radius: 9px;

    background: #ffffff;
    color: #047857;

    cursor: pointer;

    font-family: inherit;
    font-size: 8px;
    font-weight: 800;
}

.community-load-more:hover:not(:disabled) {
    background: #f0fdf4;
}

.community-load-more:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.community-button-spinner,
.community-small-spinner {
    border-radius: 50%;

    animation:
        community-rotate
        0.7s
        linear
        infinite;
}

.community-button-spinner {
    width: 10px;
    height: 10px;

    border:
        2px solid
        rgba(
            255,
            255,
            255,
            0.4
        );

    border-top-color:
        #ffffff;
}

.community-small-spinner {
    width: 9px;
    height: 9px;

    border:
        2px solid #c5e9db;

    border-top-color:
        #047857;
}

.community-spin {
    animation:
        community-rotate
        0.8s
        linear
        infinite;
}

@keyframes community-rotate {
    to {
        transform:
            rotate(360deg);
    }
}

/* ======================================================
   RESPONSIVE
====================================================== */

@media (max-width: 620px) {

    .community-intro {
        align-items: flex-start;
        flex-direction: column;
    }

    .community-refresh {
        align-self: flex-start;
    }

    .community-composer-footer {
        align-items: stretch;
        flex-direction: column;
    }

    .community-post-button {
        width: 100%;
    }

    .community-composer-tools {
        justify-content:
            space-between;
    }

    .community-preview-grid {
        grid-template-columns: 1fr;
    }

    .feed-post-card {
        padding: 14px;
        border-radius: 15px;
    }

    .feed-comment-bubble {
        max-width: 96%;
    }

    .feed-media-arrow {
        width: 31px;
        height: 31px;
    }

}

`;